import React from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Box, ButtonBase, styled, Tooltip } from '@mui/material';

const Segment = styled(ButtonBase, {
  shouldForwardProp: prop => prop !== 'selected' && prop !== 'showDivider',
})<{ selected?: boolean; showDivider?: boolean }>(({ theme, selected, showDivider }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(0.5),
  padding: theme.spacing(0.75, 1.25),
  minHeight: 32,
  fontSize: '0.8125rem',
  lineHeight: 1.2,
  fontWeight: selected ? 600 : 400,
  color: selected ? theme.palette.text.primary : theme.palette.text.secondary,
  backgroundColor: selected ? theme.palette.action.selected : 'transparent',
  borderRight: showDivider ? `1px solid ${theme.palette.divider}` : 'none',
  whiteSpace: 'nowrap',
  '&.Mui-disabled': {
    opacity: 0.5,
  },
}));

interface ModelTeamSwitchProps {
  checked: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  embedded?: boolean;
}

const ModelTeamSwitch = ({ checked, onChange, disabled = false, embedded = false }: ModelTeamSwitchProps) => {
  const selectTeam = (isSlc: boolean) => {
    if (disabled || checked === isSlc) {
      return;
    }
    onChange({
      target: { checked: isSlc },
    } as React.ChangeEvent<HTMLInputElement>);
  };

  return (
    <Tooltip title="Toggle between ARG and SLC model teams">
      <Box
        display="inline-flex"
        alignItems="stretch"
        sx={
          embedded
            ? undefined
            : {
              border: theme => `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              overflow: 'hidden',
              backgroundColor: 'background.paper',
            }
        }>
        <Segment
          selected={!checked}
          showDivider
          disabled={disabled}
          onClick={() => selectTeam(false)}
          aria-pressed={!checked}
          data-testid="model-team-switch-arg">
          {!checked && <CheckCircleIcon sx={{ fontSize: 14 }} color="inherit" />}
          ARG
        </Segment>
        <Segment
          selected={checked}
          disabled={disabled}
          onClick={() => selectTeam(true)}
          aria-pressed={checked}
          data-testid="model-team-switch-slc">
          {checked && <CheckCircleIcon sx={{ fontSize: 14 }} color="inherit" />}
          SLC
        </Segment>
      </Box>
    </Tooltip>
  );
};

export default ModelTeamSwitch;
